import { useState } from 'react';
import { toast } from 'sonner';
import { ArrowUpRight } from 'lucide-react';
import SectionMarker from '@/components/sections/SectionMarker';
import PageHero from '@/components/sections/PageHero';
import { PHOTOS, SPEAKING_TOPICS, STAN_STRATEGY_URL } from '@/constants/content';
import { SPEAKING } from '@/constants/testIds';
import { submitSpeaking } from '@/lib/api';
import { usePageMeta } from '@/lib/usePageMeta';
import { SEO } from '@/constants/seo';

export default function Speaking() {
  usePageMeta({ ...SEO.speaking });
  const [form, setForm] = useState({
    name: '', email: '', organization: '', event_name: '', event_date: '',
    event_location: '', audience_size: '', budget: '', topic: '', message: '',
  });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await submitSpeaking(form);
      toast.success('Thank you. Toia\u2019s team will be in touch.');
      setForm({
        name: '', email: '', organization: '', event_name: '', event_date: '',
        event_location: '', audience_size: '', budget: '', topic: '', message: '',
      });
    } catch (err) {
      toast.error('Could not submit. Please try again.');
    } finally { setLoading(false); }
  };

  return (
    <div className="bg-ivory">
      <section className="pt-40 lg:pt-56 pb-16">
        <div className="mx-auto max-w-[1440px] px-6 sm:px-10 lg:px-16 grid lg:grid-cols-12 gap-16 items-end">
          <div className="lg:col-span-7">
            <div className="eyebrow text-taupe mb-8">Speaking · Keynotes & Events</div>
            <h1 className="font-serif-display text-6xl md:text-7xl lg:text-[8rem] leading-[0.94] tracking-tightest text-matte">
              On stages,
              <br />
              <em className="italic text-taupe">on record.</em>
            </h1>
          </div>
          <div className="lg:col-span-5">
            <p className="text-matte/70 leading-relaxed text-[15px] max-w-md">
              Toia speaks to conferences, corporate teams, universities, and private organizations
              on artificial intelligence, entrepreneurship, and the modern founder&rsquo;s playbook.
            </p>
          </div>
        </div>
      </section>

      <section>
        <div className="mx-auto max-w-[1440px] px-6 sm:px-10 lg:px-16">
          <PageHero
            src={PHOTOS.speakingPage}
            alt="Toia Wallace speaking"
            mobileAnchor="left"
            desktopAnchor="center"
            desktopHeight="md:h-[70vh] lg:h-[86vh]"
            mobileAspect="aspect-[4/5]"
            testId="speaking-hero"
          />
        </div>
      </section>

      <section className="py-24 lg:py-32">
        <div className="mx-auto max-w-[1440px] px-6 sm:px-10 lg:px-16 grid lg:grid-cols-12 gap-16">
          <div className="lg:col-span-5">
            <SectionMarker number="01" label="Topics" />
            <h2 className="mt-8 font-serif-display text-4xl md:text-5xl lg:text-6xl tracking-tighter leading-[1.02] text-matte">
              What she talks about.
            </h2>
          </div>
          <div className="lg:col-span-7">
            <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-1">
              {SPEAKING_TOPICS.map((t, i) => (
                <li key={t} className="border-t border-taupe/30 py-6 flex items-baseline gap-6">
                  <span className="font-label text-[11px] tracking-widest uppercase text-taupe">{String(i + 1).padStart(2, '0')}</span>
                  <span className="font-serif-display text-2xl md:text-3xl text-matte tracking-tight leading-snug">{t}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="py-24 lg:py-32 bg-white">
        <div className="mx-auto max-w-[1440px] px-6 sm:px-10 lg:px-16 grid lg:grid-cols-12 gap-16">
          <div className="lg:col-span-5">
            <SectionMarker number="02" label="Audiences" />
            <h2 className="mt-8 font-serif-display text-4xl md:text-5xl lg:text-6xl tracking-tighter leading-[1.02] text-matte">
              Where she&rsquo;s been on the record.
            </h2>
          </div>
          <div className="lg:col-span-7 grid grid-cols-2 gap-x-8 gap-y-10">
            {[
              { label: 'Conferences', body: 'Global summits on AI, technology, and entrepreneurship.' },
              { label: 'Corporate', body: 'Fortune companies, boards, and executive teams.' },
              { label: 'Universities', body: 'MBA programs, business schools, and honor societies.' },
              { label: 'Founders', body: 'Private founder gatherings, retreats, and salons.' },
              { label: 'Podcasts', body: 'Long-form conversations on business and building.' },
              { label: 'Media', body: 'Interviews, panels, and editorial appearances.' },
            ].map((a) => (
              <div key={a.label}>
                <div className="eyebrow text-taupe mb-3">{a.label}</div>
                <p className="text-matte/70 text-sm leading-relaxed max-w-xs">{a.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Book Speaking Direct */}
      <section className="py-16 lg:py-24 bg-ivory">
        <div className="mx-auto max-w-[1440px] px-6 sm:px-10 lg:px-16">
          <div className="rounded-3xl bg-matte text-ivory p-10 md:p-16 grid lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-8">
              <div className="eyebrow text-gold mb-6">Direct Booking</div>
              <h3 className="font-serif-display text-3xl md:text-5xl tracking-tighter leading-tight">
                Ready to book Toia Wallace?
              </h3>
              <p className="mt-4 text-ivory/70 max-w-xl">
                Move directly to secure a date. For custom or complex engagements, use the inquiry form below.
              </p>
            </div>
            <div className="lg:col-span-4 lg:text-right">
              <a
                href="#inquiry"
                data-testid={SPEAKING.ctaBook}
                className="btn-secondary border-ivory text-ivory hover:bg-ivory hover:text-matte"
              >
                Book Toia Wallace
                <ArrowUpRight size={14} strokeWidth={1.6} />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Inquiry form */}
      <section id="inquiry" className="py-24 lg:py-32 bg-white">
        <div className="mx-auto max-w-[1440px] px-6 sm:px-10 lg:px-16 grid lg:grid-cols-12 gap-16">
          <div className="lg:col-span-5">
            <SectionMarker number="03" label="Speaking Inquiry" />
            <h2 className="mt-8 font-serif-display text-4xl md:text-5xl lg:text-6xl tracking-tighter leading-[1.02] text-matte">
              Request availability.
            </h2>
            <p className="mt-6 text-matte/70 max-w-md leading-relaxed">
              Share the details of your event and the team will respond with availability, fee, and next steps.
            </p>
          </div>
          <form onSubmit={submit} data-testid={SPEAKING.form} className="lg:col-span-7 grid sm:grid-cols-2 gap-x-8 gap-y-2">
            <Field label="Name" required value={form.name} onChange={set('name')} testId={SPEAKING.name} />
            <Field label="Email" type="email" required value={form.email} onChange={set('email')} testId={SPEAKING.email} />
            <Field label="Organization" required value={form.organization} onChange={set('organization')} testId={SPEAKING.org} />
            <Field label="Event Name" value={form.event_name} onChange={set('event_name')} testId={SPEAKING.event} />
            <Field label="Event Date" type="date" value={form.event_date} onChange={set('event_date')} testId={SPEAKING.date} />
            <Field label="Location" value={form.event_location} onChange={set('event_location')} testId={SPEAKING.location} />
            <Field label="Audience Size" value={form.audience_size} onChange={set('audience_size')} testId={SPEAKING.audience} />
            <Field label="Budget Range" value={form.budget} onChange={set('budget')} testId={SPEAKING.budget} />
            <div className="sm:col-span-2">
              <label className="block eyebrow text-taupe mb-3 mt-6">Topic Interest</label>
              <select
                value={form.topic}
                onChange={set('topic')}
                data-testid={SPEAKING.topic}
                className="w-full bg-transparent border-b border-taupe/40 py-3 text-matte focus:outline-none focus:border-gold font-serif-display text-xl"
              >
                <option value="">Select a topic</option>
                {SPEAKING_TOPICS.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="block eyebrow text-taupe mb-3 mt-6">Message</label>
              <textarea
                rows={4}
                value={form.message}
                onChange={set('message')}
                data-testid={SPEAKING.message}
                className="w-full bg-transparent border-b border-taupe/40 py-3 text-matte focus:outline-none focus:border-gold resize-none"
              />
            </div>
            <div className="sm:col-span-2 mt-8">
              <button type="submit" disabled={loading} data-testid={SPEAKING.submit} className="btn-primary disabled:opacity-50">
                {loading ? 'Submitting…' : 'Submit Inquiry'}
                <ArrowUpRight size={14} strokeWidth={1.6} />
              </button>
            </div>
          </form>
        </div>
      </section>

      {/* Secondary CTA */}
      <section className="py-24 bg-sand/40">
        <div className="mx-auto max-w-3xl px-6 text-center">
          <div className="eyebrow text-taupe mb-6">Looking for personalized guidance?</div>
          <h3 className="font-serif-display text-3xl md:text-5xl tracking-tighter leading-tight text-matte">
            Book a <em className="italic text-taupe">90-minute Strategy Intensive</em> to work directly with Toia.
          </h3>
          <div className="mt-10">
            <a href={STAN_STRATEGY_URL} target="_blank" rel="noreferrer" className="btn-primary" data-testid="speaking-cta-strategy">
              Book a Strategy Intensive
              <ArrowUpRight size={14} strokeWidth={1.6} />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({ label, testId, ...props }) {
  return (
    <label className="block">
      <span className="block eyebrow text-taupe mb-3 mt-6">{label}</span>
      <input
        {...props}
        data-testid={testId}
        className="w-full bg-transparent border-b border-taupe/40 py-3 text-matte focus:outline-none focus:border-gold font-serif-display text-xl"
      />
    </label>
  );
}
